'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import {
  ArrowUpRightIcon,
  BracketsIcon,
  CalendarIcon,
  CheckIcon,
  EditIcon,
  EyeIcon,
  FlagIcon,
  GlobeIcon,
  LanguagesIcon,
  MoreHorizontalIcon,
  SignpostIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  TextIcon,
  Trash2Icon,
  XIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { DirectionEnum, SortEnum, SortOrderEnum } from '@/enums'
import { useDeleteLocale, useLocales, useSortLocale, useBulkDeleteLocales } from '@/hooks'
import { useLocalesStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { LocalesFilterParams } from '@/types/locale'
import {
  DeleteConfirmationDialog,
  LoadingState,
  LocalesFilters,
  SortableHeader,
  TableSortActions
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { formatDateTime } from '@/lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteLocaleId, setDeleteLocaleId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const { mutate: sortLocale, isPending: isSorting } = useSortLocale()
  const { mutate: deleteLocaleMutate, isPending: isDeleting } = useDeleteLocale()
  const { mutate: bulkDeleteLocalesMutate, isPending: isBulkDeleting } = useBulkDeleteLocales()

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'LOCALES', 'CREATE')
  const canShow = hasPermission(currentUser, 'LOCALES', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'LOCALES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'LOCALES', 'DELETE')

  const selectedLocaleIds = useLocalesStore(state => state.selectedLocaleIds)
  const toggleLocaleSelection = useLocalesStore(state => state.toggleLocaleSelection)
  const selectAllLocales = useLocalesStore(state => state.selectAllLocales)
  const deselectAllLocales = useLocalesStore(state => state.deselectAllLocales)
  const getMeta = useMetaStore(state => state.getMeta)

  const filters = useMemo<LocalesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'order',
      q: params.get('q') || '',
      active: params.get('active') === 'true' ? true : params.get('active') === 'false' ? false : undefined,
      direction: params.get('direction') as DirectionEnum | undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: localesData, isLoading, error, refetch, isFetching } = useLocales(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<LocalesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString())
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<LocalesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/localization/locales')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (localeId: string) => {
    router.push(`/localization/locales/${localeId}`)
  }

  const handleEdit = (localeId: string) => {
    router.push(`/localization/locales/${localeId}/edit`)
  }

  const handleDelete = (localeId: string) => {
    setDeleteLocaleId(localeId)
  }

  const handleSortOrder = (id: string, type: SortOrderEnum) => {
    sortLocale(
      { localeId: id, type: type },
      {
        onSuccess: async updatedLocale => {
          queryClient.setQueryData(['locale', id], updatedLocale)
          await queryClient.invalidateQueries({ queryKey: ['locales'] })
          toast.success(t('sorted_successfully'))
        },
        onError: error => {
          console.error('Error sorting locale:', error)
          toast.error(t('failed_to_sort'))
        }
      }
    )
  }

  const renderActionItems = (localeId: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(localeId)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('locale_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(localeId)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_locale')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(localeId)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteLocaleId) {
      deleteLocaleMutate(deleteLocaleId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['locales'] })
          await getMeta()
          toast.success(t('deleted_successfully'))
          setDeleteLocaleId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting locale:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteLocaleId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedLocaleIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteLocales = () => {
    if (selectedLocaleIds.length > 0) {
      bulkDeleteLocalesMutate(selectedLocaleIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['locales'] })
          await getMeta()
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (localesData && localesData.items.length > 0) {
      if (selectedLocaleIds.length === localesData.items.length) {
        // If all locales are selected, deselect all
        deselectAllLocales()
      } else {
        // Otherwise, select all locales
        selectAllLocales(localesData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <LanguagesIcon className="h-5 w-5" />
                {t('locales')}
              </CardTitle>
              <CardDescription>{t('locales_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/localization/locales/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_locale')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedLocaleIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedLocaleIds.length})</span>
                  </div>
                </Button>
              )}
              <LocalesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {localesData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  localesData &&
                                  localesData.items.length > 0 &&
                                  selectedLocaleIds.length === localesData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="name"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TextIcon className="h-4 w-4" />
                            {t('name')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="code"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <BracketsIcon className="h-4 w-4" />
                            {t('code')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="countryCode"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <FlagIcon className="h-4 w-4" />
                            {t('country_code')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <SortableHeader
                            sortBy="fullCode"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <GlobeIcon className="h-4 w-4" />
                            {t('full_code')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <SortableHeader
                            sortBy="direction"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <SignpostIcon className="h-4 w-4" />
                            {t('direction')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="order"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ArrowUpRightIcon className="h-4 w-4" />
                            {t('sort_order')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <SortableHeader
                            sortBy="active"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CheckIcon className="h-4 w-4" />
                            {t('active')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {localesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        localesData.items.map((l, index) => (
                          <ContextMenu key={l.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(l.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedLocaleIds.includes(l.id)}
                                        onCheckedChange={() => toggleLocaleSelection(l.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{l.name}</span>
                                    <span className="text-muted-foreground text-xs">ID: {l.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">{l.code}</TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-row items-center justify-center gap-2">
                                    <Flag country={l.countryCode} size={16} />
                                    {l.countryCode}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">{l.fullCode}</TableCell>
                                <TableCell className="text-center">{l.direction.toUpperCase()}</TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-row items-center justify-center gap-2">
                                    <span className="text-sm">{l.order}</span>
                                    {canUpdate && (
                                      <TableSortActions
                                        onSortUp={() => handleSortOrder(l.id, SortOrderEnum.UP)}
                                        onSortDown={() => handleSortOrder(l.id, SortOrderEnum.DOWN)}
                                        disabled={isSorting}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  {l.active ? (
                                    <CheckIcon className="mx-auto h-5 w-5 text-green-500" />
                                  ) : (
                                    <XIcon className="mx-auto h-5 w-5 text-red-500" />
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(l.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(l.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(l.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(l.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={localesData.page}
                  totalPages={localesData.pages}
                  totalItems={localesData.total}
                  pageSize={localesData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteLocaleId !== null}
          onCancel={() => setDeleteLocaleId(null)}
          onConfirm={confirmDeleteMessage}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteLocales}
          isDeleting={isBulkDeleting}
        />
      )}
    </>
  )
}

export default List
